<template>
  <BaseModalLarge
    :heading="$t('products.update_product_prices')"
    :parentRoute="parentRouteName"
    testId="update-prices"
  >
    <Schedule
      v-if="showScheduleModal"
      @close="showScheduleModal = false"
      @submit="submitSchedule"
    />
    <BaseCard>
      <BaseTable
        v-if="productData.length > 0"
        :headers="[
          $t('products.product_name'),
          $t('products.current_cost'),
          $t('products.new_cost'),
          $t('products.current_sell'),
          $t('products.new_sell')
        ]"
        :rows="
          productData.map((product: any) => ({
            id: product.id,
            cells: [
              product.name,
              product.costPrice,
              product.newCostPrice,
              product.price,
              product.newSellPrice
            ]
          }))
        "
        @valueChange="valueChange"
        v-test="'updatePricesTable'"
      />
      <Flex
        v-if="productData.length < 1 && !loading"
        :class="$style.alert"
        direction="column"
        align="center"
        v-test="'noProductsAlert'"
      >
        <img
          :class="$style.image"
          src="/img/empty-states/products.svg"
        >
        <BaseText>{{ $t('global.no_results') }}</BaseText>
      </Flex>
      <LoadMore
        v-show="!allDataFetched"
        @shown="fetchMore"
      />
    </BaseCard>

    <template #footer>
      <BaseButton
        color="inverted"
        :disabled="isSaving || !productDataChanged"
        @click="onScheduleButtonClick"
        v-test="'schedulePricesButton'"
      >
        {{ $t('products.schedule_prices') }}
      </BaseButton>
      <BaseButton
        :loading="isSaving"
        :disabled="!productDataChanged"
        @click="onSubmitButtonClick"
        v-test="'updatePricesNowButton'"
      >
        {{ $t('products.update_now') }}
      </BaseButton>
    </template>
  </BaseModalLarge>
</template>

<script lang="ts">
import { flash } from '@/helpers/ui';
export default {
  name: 'UpdateProducts',
};
</script>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import type { Ref } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { useCompanyStore } from '@/stores/company';
import Schedule from './Schedule.vue';

defineProps<{
  parentRouteName: string;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const supplierId = parseInt(route.params.supplierId as string);
const productData: Ref<object[]> = ref([]);
const productDataChanged = ref(false);
const showScheduleModal = ref(false);
let scheduledAt: string | null = null;

const { loading, onResult, allDataFetched, fetchMore } = usePagination({
  fieldName: 'products',
  query: gql`
    query getProducts(
      $supplierIds: [Int!]
      $pagination: PaginationAttributes!
      $showNullSupplier: Boolean
    ) {
      products(
        supplierIds: $supplierIds
        pagination: $pagination
        showNullSupplier: $showNullSupplier
      ) {
        category {
          id
          amountProducts
          color
          name
        }
        name
        price
        costPrice
        id
        productId
      }
    }
  `,
  variables: {
    supplierIds: supplierId === 0 ? [] : [supplierId],
    showNullSupplier: supplierId === 0,
  },
});

const valueChange = () => {
  productDataChanged.value = true;
};

onResult(({ data: { products } }) => {
  productData.value = products.map((p: any) => {
    const existing = productData.value.find(
      (product: any) => product.id === p.id,
    );

    return (
      existing || {
        ...p,
        costPrice: filters.currency(p.costPrice),
        originalCostPrice: p.costPrice,
        newCostPrice: {
          editable: true,
          value: p.costPrice ? p.costPrice : 0,
          type: 'currency',
        },
        price: filters.currency(p.price),
        originalSellPrice: p.price,
        newSellPrice: {
          editable: true,
          value: p.price ? p.price : 0,
          type: 'currency',
        },
      }
    );
  });
});

const { companyId } = useCompanyStore();

const {
  mutate,
  onDone,
  loading: isSaving,
} = useMutation(
  gql`
    mutation createProductChangeSchedule(
      $input: CreateProductChangeScheduleInput!
    ) {
      createProductChangeSchedule(input: $input) {
        clientMutationId
        errors
        productChangeSchedule {
          changeAppliedAt
          changeRevertedAt
          id
          scheduledAt
        }
      }
    }
  `,
  () => ({
    variables: {
      input: {
        supplierId: supplierId || null,
        companyId,
        scheduledAt,
        productChangesAttributes: productData.value.map((p: any) => ({
          productId: p.id,
          originalCostPrice: parseInt(
            p.costPrice.substring(2).split(',').join(''),
          ),
          originalPrice: parseInt(p.price.substring(2).split(',').join('')),
          newCostPrice: p.newCostPrice.value,
          newPrice: p.newSellPrice.value,
        })),
      },
    },
    update: (cache) => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'products' });
      cache.gc();
    },
  }),
);

const onScheduleButtonClick = () => {
  showScheduleModal.value = true;
};

const onSubmitButtonClick = () => {
  mutate();
};

const submitSchedule = (dateTime: string) => {
  scheduledAt = dateTime;
  mutate();
};

onDone(() => {
  router.push({ name: scheduledAt ? 'update-product-prices' : 'products' });
  flash(
    `${t(
      `${
        scheduledAt
          ? 'global.flash.changes_scheduled'
          : 'products.products_updated'
      }`,
    )}`,
  );
});
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
}
.alert {
  margin-top: $spacing;
  margin-bottom: $spacing;
}

.image {
  width: 250px;
  max-width: 50%;
  max-height: 250px;
}
</style>
